import { Head, Link } from "@inertiajs/react"
import { Header } from "@/components/header"

export default function Privacy() {
  return (
    <div className="min-h-screen">
      <Head title="Privacy Policy" />
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <h1 className="text-2xl font-mono font-bold mb-8">PRIVACY POLICY</h1>

        <div className="prose prose-invert prose-sm max-w-none space-y-8">
          <p className="text-muted-foreground">
            Last updated: January 2026
          </p>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Overview</h2>
            <p className="text-muted-foreground leading-relaxed">
              This Privacy Policy explains how Broken Sounds ("we", "us", or "our") collects, uses,
              and protects your information when you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Information We Collect</h2>
            <div className="text-muted-foreground leading-relaxed space-y-4">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Account Information</h3>
                <p>
                  When you sign in via Discord or GitHub, we receive and store:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                  <li>Your username and display name</li>
                  <li>Your email address</li>
                  <li>Your profile picture URL</li>
                  <li>Your unique identifier from the OAuth provider</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Content You Upload</h3>
                <p>
                  When you upload samples, we store the audio files and any metadata you provide
                  (title, description, BPM, key, tags).
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-2">Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>IP address and browser information (in server logs)</li>
                  <li>Pages visited and actions taken on the site</li>
                  <li>Session cookies for authentication</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">How We Use Your Information</h2>
            <div className="text-muted-foreground leading-relaxed">
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>To provide and maintain the service</li>
                <li>To authenticate your identity</li>
                <li>To display your profile and uploaded content</li>
                <li>To communicate with you about your account</li>
                <li>To enforce our Terms of Service</li>
                <li>To respond to legal requests and prevent harm</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Information Sharing</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>We do not sell your personal information. We may share information:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>
                  <strong>Publicly:</strong> Your username, profile picture, and uploaded samples
                  are visible to all users
                </li>
                <li>
                  <strong>Service providers:</strong> We use third-party services to host files
                  and run the platform
                </li>
                <li>
                  <strong>Legal requirements:</strong> When required by law or to protect rights
                  and safety
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use essential cookies to maintain your login session. These cookies are necessary
              for the service to function and cannot be disabled.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your account information and uploaded content until you delete your account
              or request deletion. Server logs are retained for a limited period for security and
              debugging purposes.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Your Rights</h2>
            <div className="text-muted-foreground leading-relaxed space-y-3">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Download your uploaded content</li>
              </ul>
              <p>
                To exercise these rights, contact us at{" "}
                <a href="mailto:admin@brokensounds.net" className="text-primary hover:underline">
                  admin@brokensounds.net
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement reasonable security measures to protect your information. However, no
              method of transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              Broken Sounds is not intended for users under 13 years of age. We do not knowingly
              collect information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify users of
              significant changes by posting a notice on the site.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-mono font-bold mb-3">Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              For questions about this Privacy Policy, contact us at{" "}
              <a href="mailto:admin@brokensounds.net" className="text-primary hover:underline">
                admin@brokensounds.net
              </a>
            </p>
          </section>

          <div className="pt-4 border-t border-border">
            <p className="text-muted-foreground">
              See also our{" "}
              <Link href="/terms" className="text-primary hover:underline">
                Terms of Service
              </Link>
              {" and "}
              <Link href="/dmca" className="text-primary hover:underline">
                DMCA Policy
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
